import type { CSSProperties } from 'react';
import {
  EMBED_UNSHAPED_HEIGHT,
  EMBED_URL_ATTRIBUTE,
  resolveEmbedAspectRatio,
  resolveEmbedLoading,
  resolveEmbedTitle,
  toCssAspectRatio,
} from './embed-attributes';
import type { EmbedAspectRatio, EmbedLoading } from './embed-attributes';
import { resolveEmbedUrl } from './embed-providers';
import type { EmbedProvider } from './embed-providers';

/**
 * One render's worth of embed: everything `REmbed` needs, and nothing that
 * needs deciding twice.
 */
export type ResolvedEmbed = {
  provider: EmbedProvider;
  /** The rebuilt provider URL — see `embed-providers.ts`. */
  src: string;
  title: string;
  aspectRatio: EmbedAspectRatio;
  loading: EmbedLoading;
};

/**
 * The single resolution path, shared by `REmbed` and by both container
 * resolvers, in the way `r-text` shares `resolveEnclosingTag`.
 *
 * Sharing it is not tidiness. `resolveNoContainer` and
 * `resolveRendersOwnElement` both answer questions about an element that only
 * exists when a URL passed the allowlist, so if they decided that
 * independently of the component, a rejected URL would have the renderer
 * either handing column styles to an element that was never rendered or
 * wrapping nothing in a styled div. Neither would look wrong in the drawer, in
 * Redux, or in the stored JSON.
 *
 * `null` means nothing will be rendered: no URL, or one the allowlist refused.
 */
export function resolveEmbed(componentAttributes?: { [key: string]: any }): ResolvedEmbed | null {
  const resolved = resolveEmbedUrl(componentAttributes?.[EMBED_URL_ATTRIBUTE]);
  if (!resolved) {
    return null;
  }

  const { provider, src } = resolved;

  return {
    provider,
    src,
    title: resolveEmbedTitle(componentAttributes, provider.defaultTitle),
    // An author's choice wins; otherwise the provider's own shape, which is
    // what makes a pasted map 4:3 and a pasted video 16:9 without anyone
    // having to know that is what they wanted.
    aspectRatio: resolveEmbedAspectRatio(componentAttributes) ?? provider.defaultAspectRatio,
    loading: resolveEmbedLoading(componentAttributes),
  };
}

/**
 * The frame's own appearance, before column styles are laid over it.
 *
 * `display: block` removes the descender gap an inline replaced element leaves
 * under itself, and the zero border removes the 2px inset ridge every browser
 * still draws around an iframe by default.
 */
export const buildEmbedStyles = (aspectRatio: EmbedAspectRatio): CSSProperties => ({
  display: 'block',
  width: '100%',
  border: 0,
  ...(aspectRatio === 'none'
    ? { height: EMBED_UNSHAPED_HEIGHT }
    : { aspectRatio: toCssAspectRatio(aspectRatio) }),
});
